import * as THREE from 'three/webgpu';
import {
    cloneContainedMultiTargetConstraints,
    regenerateConstraintPersistentIds,
    createSketchCloneContext,
    type AbstractSketchEdgeObject,
    isAbstractSketchEdgeObject,
    isSketchArcObject,
    isSketchCircleObject,
    isSketchCollectionObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchParabolaObject,
    isSketchPointObject,
    isSketchObject,
    isSketchSlotObject,
    SketchCollectionObject,
    type SketchArcDefinition,
    type SketchCloneContext,
    type CollectSource,
    type ISketchCollect,
    type ISketchObject,
    type SketchGroup,
    type SketchPointObject,
    type SketchSlotObject,
} from '../../mesh/sketch';
import {SketchMirrorEntityGeometryAdapter} from './SketchMirrorEntityGeometryAdapter';
import {detachConstraintFromAllTargets} from '../solver/constraint/utils/detachConstraintFromAllTargets';
import type {
    SketchMirrorAxisResolvedGeometry,
    SketchMirrorEntityBaseline,
} from '../../../types/sketch/mirror/SketchMirrorAxisReferenceTypes';
import type { SketchCollectionChildMoveDetachContext } from './SketchCollectionChildMoveDetachService';
import { SketchCollectionChildMoveDetachService } from './SketchCollectionChildMoveDetachService';
import { SketchCopiedClosedCollectionBuilder } from './SketchCopiedClosedCollectionBuilder';
import {SketchSlotMoveDomainService} from './SketchSlotMoveDomainService';
import {disposeSketchEntitiesTransformCloneDomainResources} from './utils/disposeSketchEntitiesTransformCloneDomainResources';
import { SketchRectangleConstructionLineResolver } from '../rectangle';

/**
 * 草图实体变换几何服务。
 */
export class SketchEntitiesTransformGeometryService {
    /** 镜像完整实体定义的唯一适配入口。 */
    private readonly mirrorGeometryAdapter = new SketchMirrorEntityGeometryAdapter();
    /** 槽口移动域服务。 */
    private readonly slotMoveDomainService = new SketchSlotMoveDomainService();
    /** 集合子边移动拆分服务。 */
    private readonly collectionChildMoveDetachService = new SketchCollectionChildMoveDetachService();
    /** 矩形构造线归属解析服务。 */
    private readonly rectangleConstructionLineResolver = new SketchRectangleConstructionLineResolver();
    /** 闭合集合副本构建器。 */
    private readonly copiedClosedCollectionBuilder = new SketchCopiedClosedCollectionBuilder(
        this.rectangleConstructionLineResolver,
    );
    /** Copy 关闭镜向批写控制点时复用的世界坐标，避免按点创建临时向量。 */
    private readonly mirrorPointScratch = new THREE.Vector3();

    //------ 移动实体闭合集合子边拆分 开始------
    /**
     * 为移动实体命令拆分闭合集合子边。
     *
     * @param entities 当前选择实体。
     * @param sketchGroup 活动草图组。
     * @returns 拆分上下文；无须拆分时返回 null。
     */
    public detachCollectionChildEntitiesForMove(
        entities: readonly THREE.Object3D[],
        sketchGroup: SketchGroup,
    ): SketchCollectionChildMoveDetachContext | null {
        return this.collectionChildMoveDetachService.detachCollectionChildEntitiesForMove(entities, sketchGroup);
    }
    //------ 移动实体闭合集合子边拆分 结束------

    /**
     * 收集实体对应的唯一点集合。
     *
     * @param entities 草图实体列表。
     * @returns 去重后的点列表。
     */
    public collectUniquePoints(entities: readonly THREE.Object3D[]): SketchPointObject[] {
        const pointMap = new Map<string, SketchPointObject>();
        entities.forEach((entity) => {
            this.collectEntityPoints(entity, pointMap);
        });
        //------ 移动实体集合辅助对象收口 开始------
        this.collectFullySelectedSourceCollections(
            entities,
            new Set(entities.map((entity) => entity.uuid)),
        ).forEach((collection) => {
            this.collectCollectionAuxiliaryPoints(collection, pointMap);
        });
        //------ 移动实体集合辅助对象收口 结束------
        //------ 移动实体槽口轮廓整体移动 开始------
        this.slotMoveDomainService.collectSelectedSlotMovementDomains(entities).forEach((slot) => {
            this.collectEntityPoints(slot, pointMap);
        });
        //------ 移动实体槽口轮廓整体移动 结束------
        return Array.from(pointMap.values());
    }

    /**
     * 收集圆周阵列聚合关系可直接约束的有序目标。
     *
     * 普通点和基础边直接进入目标组；槽口、矩形、多边形等复合对象展开为其
     * 基础成员边。展开只改变求解关系的直接目标，不改变阵列定义保存的顶层
     * 实体身份，因此复合对象仍作为一个整体参与选择、删除和历史恢复。
     *
     * @param entities 用户选择或单个实例的顶层实体。
     * @returns 按顶层实体与内部成员顺序去重，且同时满足草图与场景对象契约的参数化目标。
     */
    public collectCircularPatternConstraintTargets(
        entities: readonly THREE.Object3D[],
    ): Array<THREE.Object3D & ISketchObject> {
        const targetMap = new Map<string, THREE.Object3D & ISketchObject>();
        const appendTarget = (target: THREE.Object3D & ISketchObject): void => {
            if (!targetMap.has(target.uuid)) {
                targetMap.set(target.uuid, target);
            }
        };
        const collectEntity = (entity: THREE.Object3D): void => {
            if (isSketchPointObject(entity) || isAbstractSketchEdgeObject(entity)) {
                appendTarget(entity);
                return;
            }
            if (isSketchSlotObject(entity)) {
                entity.getEdges().forEach((edge) => appendTarget(edge));
                return;
            }
            if (isSketchCollectionObject(entity)) {
                entity.collectSet.forEach((source) => collectEntity(source));
                this.collectCollectionAuxiliaryEdges(entity).forEach((edge) => appendTarget(edge));
            }
        };

        entities.forEach((entity) => collectEntity(entity));
        return [...targetMap.values()];
    }

    /**
      * 收集参数化刚体变换必须同时驱动的唯一控制点集合。
     *
     * 设计说明：
     * 1. {@link collectUniquePoints} 同时服务对象级复制和平移，其中圆只登记圆心，半径端点由
     *    `translateCircle()` 派生；参数系统不会调用对象级平移，因此不能复用该派生规则；
     * 2. 参数运行时会先静默写入点位，再由圆按圆心到半径端点的距离刷新半径，故圆心和半径端点
     *    必须进入同一个刚体目标，否则移动距离会被误写成半径变化；
     * 3. 完整选择集合组成边时还要展开集合辅助圆，保证多边形内切构造圆与外框保持同一位移；
     * 4. 其它实体继续复用既有唯一点域，避免复制一套直线、圆弧、椭圆、样条与槽口规则。
     *
     * @param entities 当前参数化移动或旋转的实体域。
     * @returns 去重后的参数化刚体变换控制点列表。
     */
    public collectParametricRigidTransformPoints(
        entities: readonly THREE.Object3D[],
    ): SketchPointObject[] {
        const pointMap = new Map(
            this.collectUniquePoints(entities).map((point) => [point.uuid, point]),
        );
        const selectedEntityIds = new Set(entities.map((entity) => entity.uuid));

        entities.forEach((entity) => {
            this.collectParametricCircleDefinitionPoints(entity, pointMap);
        });
        this.collectFullySelectedSourceCollections(entities, selectedEntityIds).forEach((collection) => {
            this.collectParametricCircleDefinitionPoints(collection, pointMap);
        });
        this.slotMoveDomainService.collectSelectedSlotMovementDomains(entities).forEach((slot) => {
            this.collectParametricCircleDefinitionPoints(slot, pointMap);
        });

        return Array.from(pointMap.values());
    }

    /**
     * 对实体应用矩阵变换。
     *
     * @param entities 草图实体列表。
     * @param matrix 变换矩阵。
     */
    public applyMatrixToEntities(entities: readonly THREE.Object3D[], matrix: THREE.Matrix4): void {
        //------ 复制实体圆弧平移 开始------
        const translation = this.resolveTranslationFromMatrix(matrix);
        if (translation) {
            this.applyTranslationToEntities(entities, translation);
            return;
        }
        //------ 复制实体圆弧平移 结束------

        //------ 旋转实体基线变换 开始------
        const points = this.collectUniquePoints(entities);
        const pointBaselineMap = new Map<string, THREE.Vector3>();
        points.forEach((point) => {
            pointBaselineMap.set(point.uuid, point.getPosition().clone());
        });
        points.forEach((point) => {
            const baselinePosition = pointBaselineMap.get(point.uuid);
            if (!baselinePosition) {
                return;
            }
            point.setPosition(baselinePosition.clone().applyMatrix4(matrix));
        });
        if (matrix.determinant() < 0) {
            // 反射会反转曲线参数方向；仅变换控制点会把短弧错误解释成长弧。
            this.reverseCurveDirections(entities);
        }
        //------ 旋转实体基线变换 结束------
    }

    /**
     * 反转实体域内圆弧与椭圆的参数方向。
     *
     * @param entities 待同步的草图实体域。
     */
    private reverseCurveDirections(entities: readonly THREE.Object3D[]): void {
        const transformedObjectMap = new Map<string, ISketchObject>();
        entities.forEach((entity) => {
            this.collectEntityConstraintObjects(entity, transformedObjectMap);
        });
        transformedObjectMap.forEach((object) => {
            if (isSketchArcObject(object)) {
                const definition = object.getArcDefinition();
                object.updateArcDefinition({...definition, clockwise: !definition.clockwise});
                return;
            }
            if (isSketchEllipseObject(object)) {
                const definition = object.getEllipseDefinition();
                object.updateEllipseDefinition({...definition, clockwise: !definition.clockwise});
            }
        });
    }

    /**
     * 在不执行几何约束求解的前提下对实体应用矩阵变换。
     *
     * 设计说明：
     * 1. 复制实体预览、确认、重复和 Ctrl+V 粘贴都要求副本严格落在目标位置，不能让关系求解器修正本次位移；
     * 2. 副本自身的内部关系仍需保留，因此这里只在坐标写入期间临时卸载复制域内的约束实例；
     * 3. `finally` 中把同一批约束实例原样恢复，既不创建新关系，也不会改变关系持久身份。
     *
     * @param entities 待变换的副本实体。
     * @param matrix 需要直接写入的变换矩阵。
     */
    public applyMatrixToEntitiesWithoutConstraintSolve(
        entities: readonly THREE.Object3D[],
        matrix: THREE.Matrix4,
    ): void {
        const constraintObjectMap = new Map<string, ISketchObject>();
        entities.forEach((entity) => {
            this.collectEntityConstraintObjects(entity, constraintObjectMap);
        });
        const constraintSnapshots = Array.from(constraintObjectMap.values()).map((object) => ({
            object,
            constraints: object.constraints.getRelations(),
        }));

        // 坐标写入前临时清空复制域内关系，使 SketchPointObject.setPosition 走直接写入快速路径。
        constraintSnapshots.forEach(({object}) => {
            object.constraints.clearRelations();
        });

        try {
            this.applyMatrixToEntities(entities, matrix);
        } finally {
            // 即使几何对象更新失败，也必须恢复关系容器，避免副本以无关系的半成品状态留在场景中。
            constraintSnapshots.forEach(({object, constraints}) => {
                constraints.forEach((constraint) => {
                    object.constraints.addRelation(
                        constraint as Parameters<typeof object.constraints.addRelation>[0],
                    );
                });
            });
        }
    }

    /**
     * 复制草图实体。
     *
     * @param entities 源实体列表。
     * @param sketchGroup 目标草图组。
     * @param selectedCloneMap 可选的源选择对象到副本对象映射。
     * @param persistentIdCloneMap 可选的源持久 ID 到副本持久 ID 映射。
     * @param shouldCloneConstraint 可选的调用方约束复制准入策略。
     * @param sourceObjectCloneMap 可选的真实源对象到副本对象映射。
     * @returns 已挂载到目标草图组的副本域。
     * @throws 任一实体复制、挂载、关系重建或身份重建失败时，在回滚不完整副本域后原样抛出。
     */
    public cloneEntities(
        entities: readonly THREE.Object3D[],
        sketchGroup: SketchGroup,
        selectedCloneMap?: Map<string, THREE.Object3D>,
        persistentIdCloneMap?: Map<string, string>,
        shouldCloneConstraint?: NonNullable<SketchCloneContext['shouldCloneConstraint']>,
        sourceObjectCloneMap?: Map<string, THREE.Object3D>,
    ): THREE.Object3D[] {
        /**
         * 尺寸关系展示已是容器内只读快照，不属于可克隆关系对象；
         * 此处只执行调用方对真实几何关系的复制准入策略。
         */
        const cloneContext = createSketchCloneContext((constraint) => {
            return shouldCloneConstraint?.(constraint) ?? true;
        });
        const clones: THREE.Object3D[] = [];
        const topLevelCloneMap = new Map<string, THREE.Object3D>();
        const originalSketchGroupChildren = new Set(sketchGroup.children);
        let attachedClones: THREE.Object3D[] = [];
        try {
            entities.forEach((entity) => {
                const clone = this.cloneEntity(entity, cloneContext);
                clones.push(clone);
                topLevelCloneMap.set(entity.uuid, clone);
            });
            /**
             * 复合草图对象的 `cloneSelf()` 会登记内部边和点，但槽口、闭合集合本体不属于
             * `SketchCloneContext.objectMap` 的单体草图对象契约，因此需要独立保存顶层映射。
             * 该映射同时服务用户选择恢复和复合对象本体的尺寸持久 ID 重映射。
             */
            clones.forEach((clone) => {
                sketchGroup.add(clone);
                attachedClones.push(clone);
                //------ 复制实体关联点挂载 开始------
                /**
                 * 复制草图组时，起点/终点这类直接子点会由 SketchGroup.cloneSelf() 单独复制并挂载。
                 * 复制实体只复制选中对象，因此必须把 cloneSelf 产生但尚无父对象的关联点补挂到草图组。
                 */
                this.attachDetachedRelatedPoints(clone, sketchGroup, attachedClones);
                //------ 复制实体关联点挂载 结束------
            });
            //------ 复制实体闭合轮廓重建 开始------
            attachedClones = this.rebuildCopiedClosedContours(
                entities,
                cloneContext.objectMap,
                sketchGroup,
                attachedClones,
            );
            //------ 复制实体闭合轮廓重建 结束------
            //------ 复制实体多目标关系 开始------
            /**
             * 复制草图组会在整组对象复制后统一重建多目标关系。
             * 复制实体只拥有用户选择的局部复制域，因此这里只复制目标全部落在该复制域内的关系。
             */
            const constraintSourceObjects = this.collectConstraintSourceObjects(entities, cloneContext.objectMap);
            cloneContainedMultiTargetConstraints(
                constraintSourceObjects,
                cloneContext.objectMap,
                cloneContext.shouldCloneConstraint,
            );
            //------ 复制实体多目标关系 结束------
            //------ 草图持久身份索引 开始------
            /**
             * 复制实体产出的是与源实体并存的新逻辑实体：
             * 1. 副本对象进组时由 SketchGroup 身份治愈自动换新对象 ID；
             * 2. 副本上经 cloneSelf 继承的单目标关系 ID 必须在此统一换新，保证组内关系身份唯一。
             */
            regenerateConstraintPersistentIds(Array.from(cloneContext.objectMap.values()));

            // 对外映射属于成功结果；所有复制、挂载、关系和身份步骤完成前不得暴露半成品。
            if (sourceObjectCloneMap) {
                cloneContext.objectMap.forEach((clone, sourceUuid) => {
                    sourceObjectCloneMap.set(sourceUuid, clone as unknown as THREE.Object3D);
                });
                topLevelCloneMap.forEach((clone, sourceUuid) => {
                    sourceObjectCloneMap.set(sourceUuid, clone);
                });
            }
            if (selectedCloneMap) {
                topLevelCloneMap.forEach((clone, sourceUuid) => {
                    selectedCloneMap.set(sourceUuid, clone);
                });
            }
            if (persistentIdCloneMap) {
                // 尺寸引用使用持久 ID；映射必须在副本进组完成身份治愈后读取最终 ID。
                this.collectPersistentIdentitySourceObjects(entities).forEach((sourceObject) => {
                    const clone = topLevelCloneMap.get(sourceObject.uuid)
                        ?? cloneContext.objectMap.get(sourceObject.uuid);
                    const sourcePersistentId = Reflect.get(sourceObject, 'persistentId');
                    const clonePersistentId = clone ? Reflect.get(clone, 'persistentId') : null;

                    if (typeof sourcePersistentId === 'string' && typeof clonePersistentId === 'string') {
                        persistentIdCloneMap.set(sourcePersistentId, clonePersistentId);
                    }
                });
            }
            //------ 草图持久身份索引 结束------
            return attachedClones;
        } catch (error) {
            this.rollbackIncompleteCloneDomain(
                cloneContext,
                clones,
                sketchGroup,
                originalSketchGroupChildren,
                selectedCloneMap,
                persistentIdCloneMap,
                sourceObjectCloneMap,
            );
            throw error;
        }
    }

    /**
     * 构建平移矩阵。
     *
     * @param translation 位移向量。
     * @returns 平移矩阵。
     */
    public createTranslationMatrix(translation: THREE.Vector3): THREE.Matrix4 {
        return new THREE.Matrix4().makeTranslation(translation.x, translation.y, translation.z);
    }

    /**
     * 构建绕指定中心的旋转矩阵。
     *
     * @param center 旋转中心。
     * @param axis 旋转法向。
     * @param angleRad 旋转弧度。
     * @returns 旋转矩阵。
     */
    public createRotationMatrix(center: THREE.Vector3, axis: THREE.Vector3, angleRad: number): THREE.Matrix4 {
        const toOriginMatrix = new THREE.Matrix4().makeTranslation(-center.x, -center.y, -center.z);
        const rotationMatrix = new THREE.Matrix4().makeRotationAxis(axis.clone().normalize(), angleRad);
        const backMatrix = new THREE.Matrix4().makeTranslation(center.x, center.y, center.z);
        return backMatrix.multiply(rotationMatrix).multiply(toOriginMatrix);
    }

    /**
     * 在不触发约束求解的前提下，按完整实体定义原位镜像一组草图实体。
     *
     * 该入口与确认后的实体级镜像关系复用同一个定义适配器。反射前先以同一克隆上下文捕获整组基线，
     * 因而共享端点和槽口复合结构不会在逐实体写回时被二次反射，也不会暴露半更新定义。
     *
     * @param entities 待原位镜像的真实草图实体域。
     * @param axisGeometry 当前世界镜像轴。
     * @throws 实体不支持完整定义镜像或类型写回失败时抛出错误。
     */
    public applyMirrorToEntitiesWithoutConstraintSolve(
        entities: readonly THREE.Object3D[],
        axisGeometry: SketchMirrorAxisResolvedGeometry,
    ): void {
        const baselines = this.captureMirrorEntityBaselines(entities);
        const affectedEdges = new Map<string, AbstractSketchEdgeObject>();
        const affectedOwners = new Map<string, SketchCollectionObject | SketchSlotObject>();
        this.collectMirrorVisualRefreshDomain(entities, affectedEdges, affectedOwners);

        try {
            const postRefreshedEdgeIds = this.applyMirrorDefinitionsAsBatch(baselines, entities, axisGeometry);
            // 所有定义写回完成后才刷新真实边与复合宿主，避免共享角点暴露半更新 BufferGeometry。
            this.refreshMirrorVisualDomain(affectedEdges, affectedOwners, postRefreshedEdgeIds);
        } catch (error) {
            // 镜像是一笔定义事务：任一实体失败都把已写入目标恢复到捕获前状态。
            try {
                this.restoreMirrorEntityBaselines(baselines, entities);
            } catch (rollbackError) {
                throw new AggregateError(
                    [error, rollbackError],
                    `镜像草图实体失败且视觉回滚失败：${error instanceof Error ? error.message : String(error)}`,
                );
            }
            throw error;
        } finally {
            this.disposeMirrorEntityBaselines(baselines);
        }
    }

    /**
     * 捕获可重复用于镜向预览与精确回滚的完整实体定义基线。
     * @param entities 真实草图实体域。
     * @returns 与输入顺序一一对应的独立定义快照。
     * @throws 实体类型不支持镜向，或完整定义读取、复制失败时，在释放部分基线后抛出错误。
     */
    public captureMirrorEntityBaselines(entities: readonly THREE.Object3D[]): SketchMirrorEntityBaseline[] {
        const targets = entities.map((entity) => {
            if (!isSketchObject(entity) || !this.mirrorGeometryAdapter.supports(entity)) {
                throw new Error(`镜像草图实体失败：实体不支持完整定义镜像（uuid=${entity.uuid}）。`);
            }
            return entity;
        });
        // 瞬时快照不挂场景也不改变外观，可安全复用源点材质并避免触发浏览器纹理工厂。
        const cloneContext = createSketchCloneContext(() => false, true);
        const baselines: SketchMirrorEntityBaseline[] = [];
        try {
            targets.forEach((target) => {
                const definition = this.mirrorGeometryAdapter.captureDefinition(target);
                if (!definition) {
                    throw new Error(`捕获镜向实体基线失败：无法读取完整定义（uuid=${target.uuid}）。`);
                }
                const controlPointPositions = this.mirrorGeometryAdapter.resolveControlPoints(target)
                    .map((point) => point.getPosition());
                const baseline = target.cloneSelf(cloneContext);
                /**
                 * 曲线构造器可能规范化角度或重新采样控制点；基线必须保留进入命令前的权威完整定义。
                 * 克隆仍负责维持共享点拓扑，随后统一定义协议负责把几何语义原样覆盖回来。
                 */
                if (!this.mirrorGeometryAdapter.copyDefinition(target, baseline)) {
                    throw new Error(`捕获镜向实体基线失败：无法复制完整定义（uuid=${target.uuid}）。`);
                }
                baselines.push({entity: baseline, definition, controlPointPositions});
            });
            return baselines;
        } catch (error) {
            // 基线虽不进入场景，仍走统一帧后释放队列；同时清空上下文，避免失败调用继续持有半成品。
            this.detachCloneDomainConstraints(cloneContext.objectMap.values());
            this.disposeMirrorBaselineEntities(Array.from(new Set([
                ...baselines.map((baseline) => baseline.entity),
                ...cloneContext.objectMap.values(),
            ])));
            cloneContext.pointMap.clear();
            cloneContext.objectMap.clear();
            throw error;
        }
    }

    /**
     * 把完整定义基线原样恢复到现有实体身份。
     * @param baselines 捕获时的有序定义快照。
     * @param entities 待恢复的有序运行时实体。
     * @throws 数量或类型不匹配时抛出错误。
     */
    public restoreMirrorEntityBaselines(
        baselines: readonly SketchMirrorEntityBaseline[],
        entities: readonly THREE.Object3D[],
    ): void {
        if (baselines.length !== entities.length) {
            throw new Error('恢复镜向实体基线失败：快照与实体数量不一致。');
        }
        const affectedEdges = new Map<string, AbstractSketchEdgeObject>();
        const affectedOwners = new Map<string, SketchCollectionObject | SketchSlotObject>();
        const restoredTargetEdgeIds = new Set<string>();
        this.collectMirrorVisualRefreshDomain(entities, affectedEdges, affectedOwners);
        entities.forEach((entity, index) => {
            const baseline = baselines[index];
            if (!baseline || !isSketchObject(entity)
                || !this.mirrorGeometryAdapter.restoreDefinition(entity, baseline.definition)) {
                throw new Error(`恢复镜向实体基线失败：定义类型不匹配（uuid=${entity.uuid}）。`);
            }
            this.mirrorGeometryAdapter.resolvePostSolveRefreshEdges(entity).forEach((edge) => {
                restoredTargetEdgeIds.add(edge.uuid);
            });
        });
        // 定义 setter 可能从参数重新推导共享端点；整批定义完成后以进入命令前的控制点主状态统一收口。
        entities.forEach((entity, index) => {
            const baseline = baselines[index];
            if (!baseline || !isSketchObject(entity)) {
                return;
            }
            const targetPoints = this.mirrorGeometryAdapter.resolveControlPoints(entity);
            if (targetPoints.length !== baseline.controlPointPositions.length) {
                throw new Error(`恢复镜向实体基线失败：控制点拓扑不匹配（uuid=${entity.uuid}）。`);
            }
            targetPoints.forEach((point, pointIndex) => {
                point.syncPositionSilently(baseline.controlPointPositions[pointIndex]!);
            });
        });
        // 完整定义写回已经刷新目标自身；这里只刷新共享点连接的其它边，避免反推并改写权威角度参数。
        this.refreshMirrorVisualDomain(affectedEdges, affectedOwners, restoredTargetEdgeIds);
    }

    /**
     * 先静默写入整批镜向控制点，再收口需要完整参数定义的复合实体。
     *
     * 直线共享端点可能同时属于未选中的矩形相邻边。逐实体 `apply()` 会在其它实体定义尚未完成时刷新
     * BufferGeometry，因此这里复用实体关系的控制点协议，把所有点位写完后才进入派生定义阶段。
     *
     * @param baselines 与目标顺序一一对应的权威定义快照。
     * @param entities 待原位镜向的真实实体。
     * @param axisGeometry 当前世界镜像轴。
     * @returns 已在复合定义阶段完成刷新、无需通用边刷新重复处理的边 ID。
     */
    private applyMirrorDefinitionsAsBatch(
        baselines: readonly SketchMirrorEntityBaseline[],
        entities: readonly THREE.Object3D[],
        axisGeometry: SketchMirrorAxisResolvedGeometry,
    ): Set<string> {
        if (!this.mirrorGeometryAdapter.prepareAxis(axisGeometry)) {
            throw new Error('镜像草图实体失败：镜像轴退化。');
        }

        baselines.forEach(({entity: baseline}, index) => {
            const target = entities[index];
            if (!target || !isSketchObject(target) || !this.mirrorGeometryAdapter.supportsPair(baseline, target)) {
                throw new Error(`镜像草图实体失败：基线与目标定义不匹配（uuid=${target?.uuid ?? 'unknown'}）。`);
            }
            const driverPoints = this.mirrorGeometryAdapter.resolveControlPoints(baseline);
            const drivenPoints = this.mirrorGeometryAdapter.resolveMirroredControlPoints(baseline, target);
            if (driverPoints.length !== drivenPoints.length) {
                throw new Error(`镜像草图实体失败：控制点拓扑不匹配（uuid=${target.uuid}）。`);
            }
            driverPoints.forEach((driverPoint, pointIndex) => {
                const drivenPoint = drivenPoints[pointIndex];
                if (!drivenPoint) {
                    throw new Error(`镜像草图实体失败：目标控制点缺失（uuid=${target.uuid}）。`);
                }
                driverPoint.getPositionTo(this.mirrorPointScratch);
                this.mirrorGeometryAdapter.reflectPreparedPointTo(this.mirrorPointScratch, this.mirrorPointScratch);
                drivenPoint.syncPositionSilently(this.mirrorPointScratch);
            });
        });

        const postRefreshedEdgeIds = new Set<string>();
        baselines.forEach(({entity: baseline}, index) => {
            const target = entities[index];
            if (!target || !isSketchObject(target)) {
                throw new Error(`镜像草图实体失败：目标不是草图实体（uuid=${target?.uuid ?? 'unknown'}）。`);
            }
            if (isSketchCircleObject(baseline) && isSketchCircleObject(target)) {
                // 圆的半径端点是派生定义点；点位批写后按半径规范化一次并完成圆自身刷新。
                target.setRadiusSilently(baseline.radius);
                postRefreshedEdgeIds.add(target.uuid);
                return;
            }
            if (!this.mirrorGeometryAdapter.requiresPostSolveDefinitionRefresh(target)) {
                return;
            }
            this.mirrorGeometryAdapter.resolvePostSolveRefreshEdges(target).forEach((edge) => {
                postRefreshedEdgeIds.add(edge.uuid);
            });
            this.mirrorGeometryAdapter.refreshPostSolveDefinition(baseline, target, axisGeometry);
        });
        return postRefreshedEdgeIds;
    }

    /**
     * 收集本批定义点影响的真实边与复合轮廓宿主。
     *
     * @param entities 本批待写回实体。
     * @param affectedEdges 输出的真实边集合。
     * @param affectedOwners 输出的集合或槽口宿主集合。
     */
    private collectMirrorVisualRefreshDomain(
        entities: readonly THREE.Object3D[],
        affectedEdges: Map<string, AbstractSketchEdgeObject>,
        affectedOwners: Map<string, SketchCollectionObject | SketchSlotObject>,
    ): void {
        entities.forEach((entity) => {
            if (!isSketchObject(entity) || !this.mirrorGeometryAdapter.supports(entity)) {
                return;
            }
            this.mirrorGeometryAdapter.resolveControlPoints(entity).forEach((point) => {
                point.getConnectedEdgesSnapshot().forEach((edge) => affectedEdges.set(edge.uuid, edge));
                point.getRelatedSketchObjectsSnapshot().forEach((relatedObject) => {
                    if (isSketchCollectionObject(relatedObject) || isSketchSlotObject(relatedObject)) {
                        affectedOwners.set(relatedObject.uuid, relatedObject);
                    }
                });
            });
        });
        affectedEdges.forEach((edge) => {
            const owner = Reflect.get(edge, 'closedCollection') as unknown;
            if (isSketchCollectionObject(owner) || isSketchSlotObject(owner)) {
                affectedOwners.set(owner.uuid, owner);
            }
        });
    }

    /**
     * 在定义事务完成后一次刷新共享控制点关联的全部渲染域。
     *
     * @param affectedEdges 受影响真实边。
     * @param affectedOwners 受影响复合轮廓宿主。
     * @param postRefreshedEdgeIds 已由复合定义收口刷新的边 ID。
     */
    private refreshMirrorVisualDomain(
        affectedEdges: ReadonlyMap<string, AbstractSketchEdgeObject>,
        affectedOwners: ReadonlyMap<string, SketchCollectionObject | SketchSlotObject>,
        postRefreshedEdgeIds?: ReadonlySet<string>,
    ): void {
        affectedEdges.forEach((edge) => {
            if (postRefreshedEdgeIds?.has(edge.uuid)) {
                return;
            }
            edge.refreshGeometryOnly();
        });
        affectedOwners.forEach((owner) => owner.refreshContourGeometry());
    }

    /**
     * 释放完整定义镜像临时基线独占的几何资源。
     * @param baselines 未挂入场景的临时草图副本。
     */
    public disposeMirrorEntityBaselines(baselines: readonly SketchMirrorEntityBaseline[]): void {
        this.disposeMirrorBaselineEntities(baselines.map((baseline) => baseline.entity));
    }

    /**
     * 释放尚未挂入场景的镜向基线实体资源。
     *
     * @param baselineEntities 临时实体副本。
     */
    private disposeMirrorBaselineEntities(baselineEntities: readonly ISketchObject[]): void {
        disposeSketchEntitiesTransformCloneDomainResources(
            baselineEntities.filter((baseline): baseline is ISketchObject & THREE.Object3D => (
                baseline instanceof THREE.Object3D
            )),
        );
    }

    /**
     * 构建草图平面内关于指定直线的镜向矩阵。
     *
     * @param axisPoint 镜向轴上的任意一点。
     * @param axisDirection 镜向轴方向。
     * @param sketchNormal 草图平面法向。
     * @returns 世界坐标镜向矩阵。
     * @throws 镜向轴退化或不在草图平面内时抛出错误。
     */
    public createMirrorMatrix(
        axisPoint: THREE.Vector3,
        axisDirection: THREE.Vector3,
        sketchNormal: THREE.Vector3,
    ): THREE.Matrix4 {
        const normalizedNormal = sketchNormal.clone().normalize();
        const normalizedAxis = axisDirection.clone().normalize();
        if (normalizedNormal.lengthSq() <= 1e-20 || normalizedAxis.lengthSq() <= 1e-20) {
            throw new Error('创建草图镜向矩阵失败：镜向轴或草图法向已退化。');
        }
        if (Math.abs(normalizedAxis.dot(normalizedNormal)) > 1e-8) {
            throw new Error('创建草图镜向矩阵失败：镜向轴不在当前草图平面内。');
        }

        // 轴方向与草图法向张成镜向平面；其单位法向用于标准平面反射公式。
        const mirrorPlaneNormal = normalizedAxis.clone().cross(normalizedNormal).normalize();
        const {x, y, z} = mirrorPlaneNormal;
        const reflection = new THREE.Matrix4().set(
            1 - 2 * x * x, -2 * x * y, -2 * x * z, 0,
            -2 * y * x, 1 - 2 * y * y, -2 * y * z, 0,
            -2 * z * x, -2 * z * y, 1 - 2 * z * z, 0,
            0, 0, 0, 1,
        );
        return new THREE.Matrix4().makeTranslation(axisPoint.x, axisPoint.y, axisPoint.z)
            .multiply(reflection)
            .multiply(new THREE.Matrix4().makeTranslation(-axisPoint.x, -axisPoint.y, -axisPoint.z));
    }

    /**
     * 计算实体集合的中心参考点。
     *
     * @param entities 草图实体列表。
     * @returns 参考点；无实体时返回 null。
     */
    public resolveEntitiesAnchor(entities: readonly THREE.Object3D[]): THREE.Vector3 | null {
        const points = this.collectUniquePoints(entities);
        if (points.length === 0) {
            return null;
        }

        const sum = new THREE.Vector3();
        points.forEach((point) => {
            sum.add(point.getPosition());
        });
        return sum.multiplyScalar(1 / points.length);
    }

    /**
     * 从草图组中按 ID 解析实体。
     *
     * @param sketchGroup 草图组。
     * @param entityIds 实体 ID 列表。
     * @returns 命中实体列表。
     */
    public resolveEntitiesByIds(sketchGroup: SketchGroup, entityIds: readonly string[]): THREE.Object3D[] {
        return entityIds
            .map((entityId) => sketchGroup.getObjectByProperty('uuid', entityId))
            .filter((entity): entity is THREE.Object3D => !!entity);
    }

    /**
     * 复制单个实体。
     *
     * @param entity 源实体。
     * @param cloneContext 复制上下文。
     * @returns 副本对象。
     */
    private cloneEntity(
        entity: THREE.Object3D,
        cloneContext: ReturnType<typeof createSketchCloneContext>,
    ): THREE.Object3D {
        const sketchEntity = entity as THREE.Object3D & Partial<ISketchObject> & Partial<ISketchCollect>;
        if (typeof sketchEntity.cloneSelf === 'function') {
            return sketchEntity.cloneSelf(cloneContext) as unknown as THREE.Object3D;
        }
        return entity.clone(true);
    }

    /**
     * 回滚一次未完成的实体复制域。
     *
     * @param cloneContext 本次复制上下文。
     * @param clones 已创建顶层副本。
     * @param sketchGroup 目标草图组。
     * @param originalSketchGroupChildren 复制前目标草图组的直接子对象。
     * @param selectedCloneMap 调用方选择映射。
     * @param persistentIdCloneMap 调用方持久身份映射。
     * @param sourceObjectCloneMap 调用方完整对象映射。
     */
    private rollbackIncompleteCloneDomain(
        cloneContext: SketchCloneContext,
        clones: readonly THREE.Object3D[],
        sketchGroup: SketchGroup,
        originalSketchGroupChildren: ReadonlySet<THREE.Object3D>,
        selectedCloneMap?: Map<string, THREE.Object3D>,
        persistentIdCloneMap?: Map<string, string>,
        sourceObjectCloneMap?: Map<string, THREE.Object3D>,
    ): void {
        const cloneObjects = new Set<THREE.Object3D>();
        clones.forEach((clone) => {
            cloneObjects.add(clone);
            clone.traverse((object) => cloneObjects.add(object));
        });
        cloneContext.objectMap.forEach((object) => {
            cloneObjects.add(object as unknown as THREE.Object3D);
        });
        cloneContext.pointMap.forEach((point) => cloneObjects.add(point));
        sketchGroup.children.forEach((child) => {
            if (originalSketchGroupChildren.has(child)) {
                return;
            }
            cloneObjects.add(child);
            child.traverse((object) => cloneObjects.add(object));
        });

        this.detachCloneDomainConstraints(cloneContext.objectMap.values());
        cloneObjects.forEach((object) => {
            object.parent?.remove(object);
        });

        disposeSketchEntitiesTransformCloneDomainResources(cloneObjects);

        // 对外映射只会在全部复制步骤成功后写入；失败时不得清空调用方原有条目。
        cloneContext.pointMap.clear();
        cloneContext.objectMap.clear();

        // 防止集合重建失败留下未被顶层列表承接的对象；复制前子对象快照保护既有场景内容。
        sketchGroup.children.slice().forEach((child) => {
            if (!originalSketchGroupChildren.has(child)) {
                sketchGroup.remove(child);
            }
        });
    }

    /**
     * 从复制域全部宿主解除本次新建的逻辑关系。
     * @param objects 复制上下文中的草图对象。
     */
    private detachCloneDomainConstraints(objects: Iterable<ISketchObject>): void {
        const constraints = new Set<Parameters<typeof detachConstraintFromAllTargets>[0]>();
        for (const object of objects) {
            object.constraints.getRelations().forEach((constraint) => constraints.add(constraint));
        }
        constraints.forEach(detachConstraintFromAllTargets);
    }

    /**
     * 收集单个实体涉及的点集合。
     *
     * @param entity 草图实体。
     * @param pointMap 点集合映射。
     */
    private collectEntityPoints(entity: THREE.Object3D, pointMap: Map<string, SketchPointObject>): void {
        if (isSketchPointObject(entity)) {
            pointMap.set(entity.uuid, entity);
            return;
        }

        //------ 复制实体圆派生点变换 开始------
        /**
         * 圆的半径端点与象限点由圆心、半径和 updateGeometry 派生。
         * 若把这些点与圆心一起逐个变换，会在圆心变换后再次移动派生点，导致象限点偏移。
         */
        if (isSketchCircleObject(entity)) {
            pointMap.set(entity.getCenterControlPoint().uuid, entity.getCenterControlPoint());
            return;
        }
        //------ 复制实体圆派生点变换 结束------

        //------ 圆弧拖动控制点收口 开始------
        /**
         * 圆弧拖动流程只直接操作圆心、起点、终点三个定义控制点。
         * 中点与象限点都是派生捕捉点，若进入通用点集合，会在预览恢复或变换过程中抢先改写派生点，
         * 破坏 `SketchArcObject.syncEndpointsToGeometry()` 的中心点拖动收口逻辑。
         */
        if (isSketchArcObject(entity)) {
            pointMap.set(entity.getCenterPoint().uuid, entity.getCenterPoint());
            pointMap.set(entity.getStartPoint().uuid, entity.getStartPoint());
            pointMap.set(entity.getEndPoint().uuid, entity.getEndPoint());
            return;
        }
        //------ 圆弧拖动控制点收口 结束------

        //------ 直线默认中点变换收口 开始------
        /**
         * 直线默认中点是由起点和终点派生的辅助控制点。
         * Move / Copy / Rotate 只能直接变换起点和终点，中点必须由 SketchLineObject 在端点更新后重新同步。
         */
        if (isSketchLineObject(entity)) {
            pointMap.set(entity.startPoint.uuid, entity.startPoint);
            pointMap.set(entity.endPoint.uuid, entity.endPoint);
            return;
        }
        //------ 直线默认中点变换收口 结束------

        //------ 移动实体组合对象平移收口 开始------
        if (isSketchSlotObject(entity)) {
            const edges = new Map<string, THREE.Object3D>();
            const arcDefinitions = new Map<string, SketchArcDefinition>();
            entity.getEdges().forEach((edge) => {
                this.collectTransformEdge(edge, edges, pointMap, arcDefinitions);
            });
            if (entity.constructionCenterline) {
                this.collectTransformEdge(entity.constructionCenterline, edges, pointMap, arcDefinitions);
            }
            if (entity.constructionCenterlineArc) {
                this.collectTransformEdge(entity.constructionCenterlineArc, edges, pointMap, arcDefinitions);
            }
            entity.getJunctionPointArray().forEach((point) => {
                pointMap.set(point.uuid, point);
            });
            [
                entity.centerlineStartPoint,
                entity.centerlineEndPoint,
                entity.centerPoint,
                entity.arcCenterPoint,
                entity.centerArcMidpoint,
            ].forEach((point) => {
                if (point) {
                    pointMap.set(point.uuid, point);
                }
            });
            return;
        }

        if (isSketchCollectionObject(entity)) {
            const edges = new Map<string, THREE.Object3D>();
            const arcDefinitions = new Map<string, SketchArcDefinition>();
            entity.collectSet.forEach((source) => {
                this.collectTransformEdge(source, edges, pointMap, arcDefinitions);
            });
            entity.children.forEach((child) => {
                if (isSketchPointObject(child)) {
                    pointMap.set(child.uuid, child);
                }
            });
            this.collectCollectionAuxiliaryEdges(entity).forEach((edge) => {
                this.collectTransformEdge(edge, edges, pointMap, arcDefinitions);
            });
            this.rectangleConstructionLineResolver.resolveConstructionPoints(entity).forEach((point) => {
                pointMap.set(point.uuid, point);
            });
            return;
        }
        //------ 移动实体组合对象平移收口 结束------

        if (isAbstractSketchEdgeObject(entity)) {
            entity.relatedSketchPoints.forEach((point) => {
                pointMap.set(point.uuid, point);
            });
        }

        if (isSketchCollectionObject(entity)) {
            entity.collectSet.forEach((source) => {
                this.collectEntityPoints(source, pointMap);
            });
        }

        entity.traverse((child) => {
            if (!isSketchPointObject(child)) {
                return;
            }
            pointMap.set(child.uuid, child);
        });
    }

    /**
     * 把实体域中的圆定义点补充到参数化刚体变换点域。
     *
     * @param entity 当前实体或复合对象。
     * @param pointMap 参数化刚体变换控制点映射。
     */
    private collectParametricCircleDefinitionPoints(
        entity: THREE.Object3D,
        pointMap: Map<string, SketchPointObject>,
    ): void {
        if (isSketchCircleObject(entity)) {
            const centerPoint = entity.getCenterControlPoint();
            const edgePoint = entity.getEdgeControlPoint();
            pointMap.set(centerPoint.uuid, centerPoint);
            pointMap.set(edgePoint.uuid, edgePoint);
            return;
        }

        if (isSketchSlotObject(entity)) {
            entity.getEdges().forEach((edge) => {
                this.collectParametricCircleDefinitionPoints(edge, pointMap);
            });
            if (entity.constructionCenterline) {
                this.collectParametricCircleDefinitionPoints(entity.constructionCenterline, pointMap);
            }
            if (entity.constructionCenterlineArc) {
                this.collectParametricCircleDefinitionPoints(entity.constructionCenterlineArc, pointMap);
            }
            return;
        }

        if (isSketchCollectionObject(entity)) {
            entity.collectSet.forEach((source) => {
                this.collectParametricCircleDefinitionPoints(source, pointMap);
            });
            this.collectCollectionAuxiliaryEdges(entity).forEach((edge) => {
                this.collectParametricCircleDefinitionPoints(edge, pointMap);
            });
            entity.children.forEach((child) => {
                this.collectParametricCircleDefinitionPoints(child, pointMap);
            });
            return;
        }

        entity.children.forEach((child) => {
            this.collectParametricCircleDefinitionPoints(child, pointMap);
        });
    }

    //------ 复制实体圆弧平移 开始------
    /**
     * 将纯平移矩阵解析为位移向量。
     *
     * 设计说明：
     * 1. 复制实体和移动实体的 From/To、X/Y 位移都会生成纯平移矩阵；
     * 2. 圆弧、圆这类带派生控制点的实体不能被逐点移动，否则派生点会重复触发几何重建；
     * 3. 因此先识别纯平移，再交给对象级平移方法保持实体自身的不变量。
     *
     * @param matrix 变换矩阵。
     * @returns 位移向量；非纯平移矩阵返回 null。
     */
    private resolveTranslationFromMatrix(matrix: THREE.Matrix4): THREE.Vector3 | null {
        const elements = matrix.elements;
        const epsilon = 1e-10;
        const isSame = (actual: number, expected: number): boolean => Math.abs(actual - expected) <= epsilon;
        const isTranslation =
            isSame(elements[0], 1) && isSame(elements[1], 0) && isSame(elements[2], 0) && isSame(elements[3], 0)
            && isSame(elements[4], 0) && isSame(elements[5], 1) && isSame(elements[6], 0) && isSame(elements[7], 0)
            && isSame(elements[8], 0) && isSame(elements[9], 0) && isSame(elements[10], 1) && isSame(elements[11], 0)
            && isSame(elements[15], 1);

        if (!isTranslation) {
            return null;
        }

        return new THREE.Vector3(elements[12], elements[13], elements[14]);
    }

    /**
     * 对实体集合应用对象级平移。
     *
     * 设计说明：
     * 1. 圆弧需要由 `translateArcByCenterPoint` 复用圆心拖动语义，统一平移圆心、起点、终点和派生象限点；
     * 2. 圆需要由 `translateCircle` 统一平移圆心并同步半径端点与象限点；
     * 3. 普通点和线仍沿用唯一点集合，避免共享端点被重复移动。
     *
     * @param entities 草图实体列表。
     * @param translation 位移向量。
     */
    private applyTranslationToEntities(entities: readonly THREE.Object3D[], translation: THREE.Vector3): void {
        const translatedPointIds = new Set<string>();
        const pointMap = new Map<string, SketchPointObject>();
        //------ 移动实体集合辅助对象收口 开始------
        const selectedEntityIds = new Set(entities.map((entity) => entity.uuid));
        const fullySelectedCollections = this.collectFullySelectedSourceCollections(entities, selectedEntityIds);
        const handledCollectionSourceIds = new Set<string>();
        //------ 移动实体槽口轮廓整体移动 开始------
        const handledSlotSourceIds = new Set<string>();

        this.slotMoveDomainService.collectSelectedSlotMovementDomains(entities).forEach((slot) => {
            this.slotMoveDomainService.translateSlotEntity(slot, translation);
            this.collectOwnedPointIds(slot, translatedPointIds);
            handledSlotSourceIds.add(slot.uuid);
            slot.getEdges().forEach((edge) => {
                handledSlotSourceIds.add(edge.uuid);
            });
            if (slot.constructionCenterline) {
                handledSlotSourceIds.add(slot.constructionCenterline.uuid);
            }
            if (slot.constructionCenterlineArc) {
                handledSlotSourceIds.add(slot.constructionCenterlineArc.uuid);
            }
        });
        //------ 移动实体槽口轮廓整体移动 结束------

        fullySelectedCollections.forEach((collection) => {
            /**
             * 完整闭合集合必须按集合对象整体移动。
             * 若矩形四条边被完整选中后仍逐边 `setPosition`，水平/竖直关系会在角点逐个写回时触发求解，
             * 导致 From/To 预览不再严格等于“鼠标当前点 - 抓手点”的刚体平移。
             */
            this.translateCollectionEntity(collection, translation);
            this.collectOwnedPointIds(collection, translatedPointIds);
            collection.collectSet.forEach((source) => {
                handledCollectionSourceIds.add(source.uuid);
            });
        });
        //------ 移动实体集合辅助对象收口 结束------

        entities.forEach((entity) => {
            //------ 移动实体槽口轮廓整体移动 开始------
            if (handledSlotSourceIds.has(entity.uuid)) {
                return;
            }
            //------ 移动实体槽口轮廓整体移动 结束------

            //------ 移动实体集合辅助对象收口 开始------
            if (handledCollectionSourceIds.has(entity.uuid)) {
                return;
            }
            //------ 移动实体集合辅助对象收口 结束------

            //------ 移动实体组合对象平移收口 开始------
            if (isSketchSlotObject(entity)) {
                this.slotMoveDomainService.translateSlotEntity(entity, translation);
                this.collectOwnedPointIds(entity, translatedPointIds);
                return;
            }

            if (isSketchCollectionObject(entity)) {
                this.translateCollectionEntity(entity, translation);
                this.collectOwnedPointIds(entity, translatedPointIds);
                return;
            }
            //------ 移动实体组合对象平移收口 结束------

            // 圆弧内部点具有关联语义，必须让圆弧对象自己维护半径和角度。
            if (isSketchArcObject(entity)) {
                entity.translateArcByCenterPoint(translation.clone());
                this.collectOwnedPointIds(entity, translatedPointIds);
                return;
            }

            // 圆的半径端点与象限点是派生点，必须由圆对象同步，不能参与逐点平移。
            if (isSketchCircleObject(entity)) {
                entity.translateCircle(translation.clone());
                this.collectOwnedPointIds(entity, translatedPointIds);
                return;
            }

            // 椭圆与圆弧一样带有派生象限控制点，必须由对象级平移保持半轴和旋转角不变。
            if (isSketchEllipseObject(entity)) {
                entity.translateEllipseByCenterPoint(translation.clone());
                this.collectOwnedPointIds(entity, translatedPointIds);
                return;
            }

            // 抛物线的起终点由定义参数反解，必须走对象级平移保持焦点、顶点与参数区间一致。
            if (isSketchParabolaObject(entity)) {
                entity.translateParabolaByDefinition(translation.clone());
                this.collectOwnedPointIds(entity, translatedPointIds);
                return;
            }

            this.collectEntityPoints(entity, pointMap);
        });

        pointMap.forEach((point) => {
            // 已经由圆弧/圆对象移动过的共享点不能再次移动，否则会造成长度或半径变化。
            if (translatedPointIds.has(point.uuid)) {
                return;
            }
            point.setPosition(point.getPosition().clone().add(translation));
        });
    }

    //------ 移动实体集合辅助对象收口 开始------
    /**
     * 收集集合直接挂载的辅助对象点。
     *
     * 设计说明：
     * 1. 多边形辅助圆直接挂在集合 children 下，不属于 collectSet；
     * 2. 当移动命令选中的是完整多边形边集合时，也必须把这些辅助对象纳入预览基线；
     * 3. 这里只收集直接子级辅助边，避免递归误收集其它独立草图对象。
     *
     * @param collection 集合对象。
     * @param pointMap 点集合映射。
     */
    private collectCollectionAuxiliaryPoints(
        collection: SketchCollectionObject,
        pointMap: Map<string, SketchPointObject>,
    ): void {
        const edges = new Map<string, THREE.Object3D>();
        const arcDefinitions = new Map<string, SketchArcDefinition>();
        this.collectCollectionAuxiliaryEdges(collection).forEach((edge) => {
            this.collectTransformEdge(edge, edges, pointMap, arcDefinitions);
        });
        this.rectangleConstructionLineResolver.resolveConstructionPoints(collection).forEach((point) => {
            pointMap.set(point.uuid, point);
        });
    }

    //------ 移动实体集合辅助对象收口 结束------

    //------ 移动实体组合对象平移收口 开始------
    /**
     * 平移集合对象及其直接辅助边。
     *
     * 设计说明：
     * 1. 集合对象可能包含圆弧、样条以及不属于 collectSet 的辅助构造边；
     * 2. 这些对象不能简单逐点 setPosition，否则圆弧中心点会触发一次对象级联动，随后端点再被逐点移动，形成二次位移；
     * 3. 因此先静默平移定义点，再按边对象统一刷新几何，让派生中点、象限点和轮廓在对象内部同步。
     *
     * @param collection 待平移集合对象。
     * @param translation 位移向量。
     */
    private translateCollectionEntity(collection: SketchCollectionObject, translation: THREE.Vector3): void {
        const edges = new Map<string, THREE.Object3D>();
        const points = new Map<string, SketchPointObject>();
        const arcDefinitions = new Map<string, SketchArcDefinition>();

        collection.collectSet.forEach((source) => {
            this.collectTransformEdge(source, edges, points, arcDefinitions);
        });

        collection.children.forEach((child) => {
            if (isSketchPointObject(child)) {
                points.set(child.uuid, child);
            }
        });
        this.collectCollectionAuxiliaryEdges(collection).forEach((edge) => {
            this.collectTransformEdge(edge, edges, points, arcDefinitions);
        });
        this.rectangleConstructionLineResolver.resolveConstructionPoints(collection).forEach((point) => {
            points.set(point.uuid, point);
        });

        this.translatePointsSilently(points, translation);
        this.refreshTranslatedEdges(edges, translation, arcDefinitions);
        collection.refreshContourGeometry();
    }

    //------ 矩形构造线移动实体平移 开始------
    /**
     * 收集集合关联的辅助边。
     *
     * 设计说明：
     * 1. 多边形辅助圆等对象直接挂在集合 children 下；
     * 2. 矩形构造线按真实创建链路挂在草图组下，并通过 `geometryData.constructionLines.lineIds` 关联；
     * 3. 移动实体需要把这两类辅助对象统一纳入基线和对象级平移，避免矩形移动后构造线留在原位。
     *
     * @param collection 集合对象。
     * @returns 去重后的真实草图辅助边对象列表。
     */
    private collectCollectionAuxiliaryEdges(collection: SketchCollectionObject): AbstractSketchEdgeObject[] {
        const edgeMap = new Map<string, AbstractSketchEdgeObject>();
        collection.children.forEach((child) => {
            if (!isAbstractSketchEdgeObject(child) || collection.collectSet.has(child as CollectSource)) {
                return;
            }
            edgeMap.set(child.uuid, child);
        });
        this.rectangleConstructionLineResolver.resolveConstructionLines(collection).forEach((line) => {
            edgeMap.set(line.uuid, line);
        });
        return Array.from(edgeMap.values());
    }
    //------ 矩形构造线移动实体平移 结束------

    /**
     * 收集需要静默平移的边和定义点。
     *
     * @param edge 草图边对象。
     * @param edges 边对象集合。
     * @param points 定义点集合。
     * @param arcDefinitions 圆弧旧定义集合。
     */
    private collectTransformEdge(
        edge: THREE.Object3D,
        edges: Map<string, THREE.Object3D>,
        points: Map<string, SketchPointObject>,
        arcDefinitions: Map<string, SketchArcDefinition>,
    ): void {
        edges.set(edge.uuid, edge);

        if (isSketchLineObject(edge)) {
            points.set(edge.startPoint.uuid, edge.startPoint);
            points.set(edge.endPoint.uuid, edge.endPoint);
            return;
        }

        if (isSketchArcObject(edge)) {
            arcDefinitions.set(edge.uuid, edge.getArcDefinition());
            points.set(edge.getCenterPoint().uuid, edge.getCenterPoint());
            points.set(edge.getStartPoint().uuid, edge.getStartPoint());
            points.set(edge.getEndPoint().uuid, edge.getEndPoint());
            return;
        }

        if (isSketchCircleObject(edge)) {
            points.set(edge.getCenterControlPoint().uuid, edge.getCenterControlPoint());
            return;
        }

        if (isSketchEllipseObject(edge)) {
            points.set(edge.getCenterPoint().uuid, edge.getCenterPoint());
            points.set(edge.getStartPoint().uuid, edge.getStartPoint());
            points.set(edge.getEndPoint().uuid, edge.getEndPoint());
            edge.getQuadrantPoints().forEach((point) => {
                points.set(point.uuid, point);
            });
            return;
        }

        if (isAbstractSketchEdgeObject(edge)) {
            edge.relatedSketchPoints.forEach((point) => {
                points.set(point.uuid, point);
            });
        }
    }

    /**
     * 静默平移点集合。
     *
     * @param points 点集合。
     * @param translation 位移向量。
     */
    private translatePointsSilently(points: ReadonlyMap<string, SketchPointObject>, translation: THREE.Vector3): void {
        const baselines = new Map<string, THREE.Vector3>();
        points.forEach((point) => {
            baselines.set(point.uuid, point.getPosition());
        });

        points.forEach((point) => {
            const baseline = baselines.get(point.uuid);
            if (!baseline) {
                return;
            }
            point.syncPositionSilently(baseline.clone().add(translation));
        });
    }

    /**
     * 刷新静默平移后的边对象。
     *
     * @param edges 边对象集合。
     * @param translation 位移向量。
     * @param arcDefinitions 圆弧旧定义集合。
     */
    private refreshTranslatedEdges(
        edges: ReadonlyMap<string, THREE.Object3D>,
        translation: THREE.Vector3,
        arcDefinitions: ReadonlyMap<string, SketchArcDefinition>,
    ): void {
        edges.forEach((edge) => {
            if (isSketchArcObject(edge)) {
                const definition = arcDefinitions.get(edge.uuid);
                if (!definition) {
                    throw new Error('移动实体刷新圆弧失败：缺少圆弧基线定义。');
                }
                edge.updateArcDefinition({
                    ...definition,
                    centerWorld: definition.centerWorld.clone().add(translation),
                });
                return;
            }

            if (isSketchCircleObject(edge)) {
                edge.setRadiusSilently(edge.radius);
                return;
            }

            if (isSketchLineObject(edge) || isAbstractSketchEdgeObject(edge)) {
                edge.syncEndpointsToGeometry();
            }
        });
    }
    //------ 移动实体组合对象平移收口 结束------

    /**
     * 收集对象已负责移动的点 ID。
     *
     * @param entity 已执行对象级平移的实体。
     * @param pointIds 点 ID 集合。
     */
    private collectOwnedPointIds(entity: THREE.Object3D, pointIds: Set<string>): void {
        //------ 移动实体组合对象平移收口 开始------
        if (isSketchSlotObject(entity) || isSketchCollectionObject(entity)) {
            const ownedPoints = new Map<string, SketchPointObject>();
            this.collectEntityPoints(entity, ownedPoints);
            ownedPoints.forEach((point) => {
                pointIds.add(point.uuid);
            });
            return;
        }
        //------ 移动实体组合对象平移收口 结束------

        if (isSketchPointObject(entity)) {
            pointIds.add(entity.uuid);
        }

        if (isAbstractSketchEdgeObject(entity)) {
            entity.relatedSketchPoints.forEach((point) => {
                pointIds.add(point.uuid);
            });
        }

        entity.traverse((child) => {
            if (!isSketchPointObject(child)) {
                return;
            }
            pointIds.add(child.uuid);
        });
    }
    //------ 复制实体圆弧平移 结束------

    //------ 复制实体关联点挂载 开始------
    /**
     * 将副本实体中尚未挂载到对象树的关联点补挂到草图组。
     *
     * 设计说明：
     * 1. 直线的起点/终点属于 related points，不是直线对象的 child；
     * 2. 复制整个草图时这些点会作为草图组直接子对象被复制；
     * 3. 复制单个实体时必须在服务层补齐同样的挂载语义，确保选择、历史快照和对象跟踪能看到端点。
     *
     * @param clone 副本实体。
     * @param sketchGroup 目标草图组。
     * @param attachedClones 已挂载副本集合。
     */
    private attachDetachedRelatedPoints(
        clone: THREE.Object3D,
        sketchGroup: SketchGroup,
        attachedClones: THREE.Object3D[],
    ): void {
        if (!isAbstractSketchEdgeObject(clone)) {
            return;
        }
        if (isSketchCircleObject(clone)) {
            return;
        }

        clone.relatedSketchPoints.forEach((point) => {
            if (point.parent) {
                return;
            }
            sketchGroup.add(point);
            attachedClones.push(point);
        });
    }
    //------ 复制实体关联点挂载 结束------

    //------ 复制实体闭合轮廓重建 开始------
    /**
     * 在复制完成后重建仍然完整的闭合轮廓集合。
     *
     * 设计说明：
     * 1. 轮廓点击在 UI 中仍按组成边展示，用户可以移除其中任意一条边；
     * 2. 因此不能在选择阶段直接把轮廓提升成集合对象，否则会丢失“移除组成部分”的交互语义；
     * 3. 复制完成后再根据实际选择集合判断：只有源集合的所有组成边都被复制时，副本才重新收口为集合对象。
     *
     * @param sourceEntities 本次复制的源实体。
     * @param objectMap 源对象到副本对象映射。
     * @param sketchGroup 目标草图组。
     * @param attachedClones 已挂载到草图组的副本对象。
     * @returns 重建闭合轮廓后的顶层副本对象列表。
     */
    private rebuildCopiedClosedContours(
        sourceEntities: readonly THREE.Object3D[],
        objectMap: ReadonlyMap<string, ISketchObject>,
        sketchGroup: SketchGroup,
        attachedClones: readonly THREE.Object3D[],
    ): THREE.Object3D[] {
        const selectedSourceIds = new Set(sourceEntities.map((entity) => entity.uuid));
        const sourceCollections = this.collectFullySelectedSourceCollections(sourceEntities, selectedSourceIds);
        if (sourceCollections.length === 0) {
            return [...attachedClones];
        }

        const rebuiltSourceCloneIds = new Set<string>();
        const rebuiltCollections = sourceCollections
            .map((sourceCollection) => this.createCopiedClosedCollection(sourceCollection, objectMap, rebuiltSourceCloneIds))
            .filter((collection): collection is SketchCollectionObject => collection !== null);

        rebuiltCollections.forEach((collection) => {
            sketchGroup.add(collection);
        });

        return [
            ...attachedClones.filter((clone) => !rebuiltSourceCloneIds.has(clone.uuid)),
            ...rebuiltCollections,
        ];
    }

    /**
     * 收集当前选择中仍然完整覆盖的源闭合集合。
     *
     * @param sourceEntities 本次复制的源实体。
     * @param selectedSourceIds 本次复制的源实体 ID 集合。
     * @returns 完整覆盖的源闭合集合列表。
     */
    private collectFullySelectedSourceCollections(
        sourceEntities: readonly THREE.Object3D[],
        selectedSourceIds: ReadonlySet<string>,
    ): SketchCollectionObject[] {
        const collectionMap = new Map<string, SketchCollectionObject>();
        sourceEntities.forEach((entity) => {
            const sourceCollection = this.resolveSourceClosedCollection(entity);
            if (!sourceCollection || collectionMap.has(sourceCollection.uuid)) {
                return;
            }
            const collectionSources = Array.from(sourceCollection.collectSet);
            const isFullySelected = collectionSources.length > 0
                && collectionSources.every((source) => selectedSourceIds.has(source.uuid));
            if (!isFullySelected) {
                return;
            }
            collectionMap.set(sourceCollection.uuid, sourceCollection);
        });
        return Array.from(collectionMap.values());
    }

    /**
     * 基于源闭合集合创建副本闭合集合。
     *
     * @param sourceCollection 源闭合集合。
     * @param objectMap 源对象到副本对象映射。
     * @param rebuiltSourceCloneIds 已被新集合接管的副本源边 ID 集合。
     * @returns 新闭合集合；副本缺失时返回 null。
     */
    private createCopiedClosedCollection(
        sourceCollection: SketchCollectionObject,
        objectMap: ReadonlyMap<string, ISketchObject>,
        rebuiltSourceCloneIds: Set<string>,
    ): SketchCollectionObject | null {
        return this.copiedClosedCollectionBuilder.build(
            sourceCollection,
            objectMap,
            rebuiltSourceCloneIds,
        );
    }

    /**
     * 解析源实体所属的闭合集合。
     *
     * @param entity 源实体。
     * @returns 源实体所属的闭合集合；不属于集合时返回 null。
     */
    private resolveSourceClosedCollection(entity: THREE.Object3D): SketchCollectionObject | null {
        if (!isAbstractSketchEdgeObject(entity)) {
            return null;
        }
        const closedCollection = Reflect.get(entity, 'closedCollection') as unknown;
        return isSketchCollectionObject(closedCollection) ? closedCollection : null;
    }
    //------ 复制实体闭合轮廓重建 结束------

    //------ 复制实体多目标关系 开始------
    /**
     * 收集当前复制域中可承载约束的源对象。
     *
     * 设计说明：
     * 1. 与 `SketchGroup.cloneSelf()` 一样，多目标关系必须在全部对象复制完成后统一复制；
     * 2. 这里以 `objectMap` 作为边界，只收集已经成功建立副本映射的源对象；
     * 3. 这样可避免选中局部实体时，把副本错误地约束到未复制的源对象。
     *
     * @param entities 源实体列表。
     * @param objectMap 源对象到副本对象映射。
     * @returns 可复制多目标关系的源对象列表。
     */
    private collectConstraintSourceObjects(
        entities: readonly THREE.Object3D[],
        objectMap: ReadonlyMap<string, ISketchObject>,
    ): ISketchObject[] {
        const sourceObjectMap = new Map<string, ISketchObject>();
        entities.forEach((entity) => {
            this.collectEntityConstraintObjects(entity, sourceObjectMap);
        });
        return Array.from(sourceObjectMap.values())
            .filter((sourceObject) => objectMap.has(sourceObject.uuid));
    }

    /**
     * 收集复制域内具有持久身份的源对象。
     *
     * 设计说明：
     * 1. 尺寸引用可能指向实体本体、控制点或槽口等组合对象；
     * 2. 单纯遍历 Three.js 子树不足以覆盖边对象维护的关联点和集合 `collectSet`；
     * 3. 统一递归收集后，再以复制上下文为准输出最终持久 ID 映射。
     *
     * @param entities 源实体列表。
     * @returns 按运行时 UUID 去重的源对象列表。
     */
    private collectPersistentIdentitySourceObjects(
        entities: readonly THREE.Object3D[],
    ): THREE.Object3D[] {
        const sourceObjectMap = new Map<string, THREE.Object3D>();
        const collect = (object: THREE.Object3D): void => {
            if (typeof Reflect.get(object, 'persistentId') === 'string') {
                sourceObjectMap.set(object.uuid, object);
            }
            if (isAbstractSketchEdgeObject(object)) {
                object.relatedSketchPoints.forEach((point) => collect(point));
            }
            if (isSketchCollectionObject(object)) {
                object.collectSet.forEach((source) => collect(source));
            }
            object.children.forEach((child) => collect(child));
        };

        entities.forEach((entity) => collect(entity));
        return Array.from(sourceObjectMap.values());
    }

    /**
     * 收集单个实体内可承载约束的源对象。
     *
     * @param entity 源实体。
     * @param sourceObjectMap 源对象集合。
     */
    private collectEntityConstraintObjects(
        entity: THREE.Object3D,
        sourceObjectMap: Map<string, ISketchObject>,
    ): void {
        if (isSketchPointObject(entity) || isAbstractSketchEdgeObject(entity)) {
            sourceObjectMap.set(entity.uuid, entity);
        }

        if (isAbstractSketchEdgeObject(entity)) {
            entity.relatedSketchPoints.forEach((point) => {
                sourceObjectMap.set(point.uuid, point);
            });
        }

        if (isSketchCollectionObject(entity)) {
            entity.collectSet.forEach((source) => {
                this.collectEntityConstraintObjects(source, sourceObjectMap);
            });
        }

        entity.traverse((child) => {
            if (!isSketchPointObject(child) && !isAbstractSketchEdgeObject(child)) {
                return;
            }
            sourceObjectMap.set(child.uuid, child);
        });
    }
    //------ 复制实体多目标关系 结束------
}
